import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, onBeforeUnmount } from "vue";
  import { useRoute } from "vue-router";
  import { useToast } from "vue-toastification";
  import optionalUpgradeApi from "@/services/api/optionalUpgradeApi";
  import { EstateOptionalUpgrade } from "@/types/OptionalUpgradeInterface";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminEstateOptionalUpgrades',
  setup(__props, { expose: __expose }) {
  __expose();

  const route = useRoute();
  const toast = useToast();
  const estateId = route.params.estateId as string;

  const loading = ref(true);
  const saving = ref(false);
  const showModal = ref(false);
  const isEditMode = ref(false);
  const upgrades = ref<EstateOptionalUpgrade[]>([]);
  const editingId = ref<string | null>(null);

  const form = ref({ name: "", description: "", price: 0 });
  const nameError = ref(false);
  const priceError = ref(false);

  const fileInputRef = ref<HTMLInputElement | null>(null);
  const selectedFile = ref<File | null>(null);
  const imagePreviewUrl = ref<string>("");
  const imageRemoved = ref(false);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      maximumFractionDigits: 0,
    }).format(price);

  const fetchUpgrades = async () => {
    try {
      loading.value = true;
      upgrades.value = await optionalUpgradeApi.getOptionalUpgradesByEstateId(
        estateId
      );
    } catch {
      toast.error("Failed to load optional upgrades");
    } finally {
      loading.value = false;
    }
  };

  onMounted(fetchUpgrades);

  onBeforeUnmount(() => {
    if (imagePreviewUrl.value.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreviewUrl.value);
    }
  });

  const openCreateModal = () => {
    isEditMode.value = false;
    editingId.value = null;
    form.value = { name: "", description: "", price: 0 };
    nameError.value = false;
    priceError.value = false;
    selectedFile.value = null;
    imagePreviewUrl.value = "";
    imageRemoved.value = false;
    showModal.value = true;
  };

  const openEditModal = (upgrade: EstateOptionalUpgrade) => {
    isEditMode.value = true;
    editingId.value = upgrade._id;
    form.value = {
      name: upgrade.name,
      description: upgrade.description || "",
      price: upgrade.price,
    };
    nameError.value = false;
    priceError.value = false;
    selectedFile.value = null;
    imagePreviewUrl.value = upgrade.url || "";
    imageRemoved.value = false;
    showModal.value = true;
  };

  const closeModal = () => {
    if (imagePreviewUrl.value.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreviewUrl.value);
    }

    imageRemoved.value = false;
    showModal.value = false;
  };

  const saveUpgrade = async () => {
    nameError.value = !form.value.name.trim();
    priceError.value =
      form.value.price === null || form.value.price === undefined;

    if (nameError.value || priceError.value) return;

    saving.value = true;

    try {
      let imageUrl: string | undefined;

      if (imageRemoved.value) {
        imageUrl = "";
      } else if (selectedFile.value) {
        const formData = new FormData();

        formData.append("files", selectedFile.value);
        const results =
          await optionalUpgradeApi.uploadEstateOptionalUpgradeImage(
            estateId,
            formData
          );

        imageUrl = results[0]?.Location || imagePreviewUrl.value || undefined;
      } else {
        imageUrl = imagePreviewUrl.value || undefined;
      }

      if (isEditMode.value && editingId.value) {
        await optionalUpgradeApi.updateEstateOptionalUpgrade(editingId.value, {
          name: form.value.name,
          description: form.value.description || undefined,
          price: form.value.price,
          url: imageUrl,
        });
        toast.success("Upgrade updated");
      } else {
        await optionalUpgradeApi.createEstateOptionalUpgrade({
          name: form.value.name,
          description: form.value.description || undefined,
          price: form.value.price,
          url: imageUrl,
          estateId,
        });
        toast.success("Upgrade created");
      }

      closeModal();
      await fetchUpgrades();
    } catch {
      toast.error("Failed to save upgrade");
    } finally {
      saving.value = false;
    }
  };

  const onFileSelected = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    selectedFile.value = file;
    imageRemoved.value = false;

    if (imagePreviewUrl.value.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreviewUrl.value);
    }

    imagePreviewUrl.value = URL.createObjectURL(file);
  };

  const clearImage = () => {
    if (imagePreviewUrl.value.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreviewUrl.value);
    }

    selectedFile.value = null;
    imagePreviewUrl.value = "";
    imageRemoved.value = true;

    if (fileInputRef.value) fileInputRef.value.value = "";
  };

  const confirmDelete = async (upgrade: EstateOptionalUpgrade) => {
    if (!confirm(`Delete "${upgrade.name}"?`)) return;

    try {
      await optionalUpgradeApi.deleteEstateOptionalUpgrade(upgrade._id);
      toast.success("Upgrade deleted");
      await fetchUpgrades();
    } catch {
      toast.error("Failed to delete upgrade");
    }
  };

const __returned__ = { route, toast, estateId, loading, saving, showModal, isEditMode, upgrades, editingId, form, nameError, priceError, fileInputRef, selectedFile, imagePreviewUrl, imageRemoved, formatPrice, fetchUpgrades, openCreateModal, openEditModal, closeModal, saveUpgrade, onFileSelected, clearImage, confirmDelete, ref, onMounted, onBeforeUnmount, get useRoute() { return useRoute }, get useToast() { return useToast }, get optionalUpgradeApi() { return optionalUpgradeApi }, get EstateOptionalUpgrade() { return EstateOptionalUpgrade } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})